import { PlacedBunkerState, generateId } from './layoutState';
import { TilesetKey, BUNKER_CATALOG } from './bunkerCatalog';
import { FieldOrientation } from '../components/FieldCanvas';

// ── Coordinate conversion notes ──────────────────────────────────────────────
//
// Editor (feet, origin = field center):
//   xFt  : field width axis,  -60 … +60
//   zFt  : field length axis, -75 … +75  (0 = centerline)
//   rotationDeg : Konva canvas rotation, clockwise from sprite "up"
//
// Unity (units, origin = field center, 1 unit = 3 ft):
//   position.x = xFt / 3   (width axis)
//   position.y = 0          (ground)
//   position.z = zFt / 3   (length axis)
//
// Rotation — horizontal canvas mode (the default):
//   canvas-X = game-Z,  canvas-Y = game-X
//   canvas 0° "up" points to game -X = Unity 270°
//   → export: Unity Y = (canvas_rot + 270) % 360
//   → import: canvas_rot = (Unity_Y + 90)  % 360   [inverse of +270]
//
// Rotation — vertical canvas mode:
//   canvas-X = game-X,  canvas-Y = game-Z
//   canvas 0° "up" points to game -Z = Unity 180°
//   → export: Unity Y = (canvas_rot + 180) % 360
//   → import: canvas_rot = (Unity_Y + 180) % 360   [inverse of +180]
//
// ─────────────────────────────────────────────────────────────────────────────

interface ArenaObjectSpec {
  type: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  team: number;
  symmetric: boolean;
}

export interface ArenaSpec {
  version: number;
  symmetry: string;
  size: { width: number; length: number };
  tileset: string;
  team1Color: string;
  team2Color: string;
  objects: ArenaObjectSpec[];
  botPositions: unknown[];
  // Extra metadata written by this editor; ignored by ArenaLoader (Newtonsoft ignores unknown fields).
  _editorMeta?: { orientation: FieldOrientation };
}

// ── Export ────────────────────────────────────────────────────────────────────

function canvasRotToUnityY(rotDeg: number, orientation: FieldOrientation): number {
  const offset = orientation === 'horizontal' ? 270 : 180;
  return (rotDeg + offset) % 360;
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

export function buildArenaSpec(
  bunkers: PlacedBunkerState[],
  tileset: TilesetKey,
  orientation: FieldOrientation,
): ArenaSpec {
  return {
    version: 1,
    symmetry: 'NONE',
    size: { width: 40, length: 50 },
    tileset,
    team1Color: 'default',
    team2Color: 'default',
    _editorMeta: { orientation },
    objects: [
      // Explicit starter brick for each team at the canonical end-zone positions.
      { type: 'STARTER_BRICK', position: { x: 0, y: 0, z:  24.5 }, rotation: { x: 0, y:   0, z: 0 }, team: 1, symmetric: false },
      { type: 'STARTER_BRICK', position: { x: 0, y: 0, z: -24.5 }, rotation: { x: 0, y: 180, z: 0 }, team: 2, symmetric: false },
      ...bunkers.map(b => ({
        type: b.type,
        position: {
          x: round4(b.xFt / 3),
          y: 0,
          z: round4(b.zFt / 3),
        },
        rotation: {
          x: 0,
          y: round4(canvasRotToUnityY(b.rotationDeg, orientation)),
          z: 0,
        },
        team: 0,
        symmetric: false,
      })),
    ],
    botPositions: [],
  };
}

export function downloadArenaJson(
  bunkers: PlacedBunkerState[],
  tileset: TilesetKey,
  orientation: FieldOrientation,
  name: string,
): void {
  const spec = buildArenaSpec(bunkers, tileset, orientation);
  const json = JSON.stringify(spec, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${name}.arena.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Import ────────────────────────────────────────────────────────────────────

// Object types that are auto-generated and should not be round-tripped through the editor.
const SKIP_TYPES = new Set(['STARTER_BRICK', 'BUTTON', 'SPAWNPOINT']);

// All user-placeable types in the current catalog.
const CATALOG_TYPES = new Set(BUNKER_CATALOG.map(b => b.type));

export interface ImportResult {
  bunkers: PlacedBunkerState[];
  tileset: TilesetKey;
  skipped: number;    // count of objects not imported (unknown/auto types)
  error?: string;
}

function unityYToCanvasRot(unityY: number, orientation: FieldOrientation): number {
  // Inverse of canvasRotToUnityY: subtract the same offset mod 360.
  const offset = orientation === 'horizontal' ? 270 : 180;
  return (unityY - offset + 360) % 360;
}

const VALID_TILESETS: TilesetKey[] = ['ORIGINAL', 'AIR_NXL', 'AIR_VRML'];

export function importArenaJson(text: string): ImportResult {
  let spec: any;
  try {
    spec = JSON.parse(text);
  } catch {
    return { bunkers: [], tileset: 'ORIGINAL', skipped: 0, error: 'File is not valid JSON.' };
  }

  if (!Array.isArray(spec?.objects)) {
    return { bunkers: [], tileset: 'ORIGINAL', skipped: 0, error: 'No "objects" array found in file.' };
  }

  // Read back the orientation used at export time, defaulting to horizontal.
  const orientation: FieldOrientation = spec._editorMeta?.orientation ?? 'horizontal';

  const tileset: TilesetKey = VALID_TILESETS.includes(spec.tileset)
    ? spec.tileset as TilesetKey
    : 'ORIGINAL';

  const bunkers: PlacedBunkerState[] = [];
  let skipped = 0;

  for (const obj of spec.objects) {
    if (!obj?.type) { skipped++; continue; }
    if (SKIP_TYPES.has(obj.type)) continue;           // silently skip auto-placed objects
    if (!CATALOG_TYPES.has(obj.type)) { skipped++; continue; } // unknown type

    const xFt = round4((obj.position?.x ?? 0) * 3);
    const zFt = round4((obj.position?.z ?? 0) * 3);
    const rotationDeg = unityYToCanvasRot(obj.rotation?.y ?? 0, orientation);

    bunkers.push({ id: generateId(), type: obj.type, xFt, zFt, rotationDeg });
  }

  return { bunkers, tileset, skipped };
}

/** Strip characters that aren't safe in filenames. */
export function sanitizeName(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9 _-]/g, '').trim();
}

// ── Upload validation ────────────────────────────────────────────────────────
//
// Field bounds in Unity units (the [-60,60]ft / [-75,75]ft bounds from
// fieldCoords.clampField(), divided by 3 ft/unit).
const MAX_X_UNITS = 20;
const MAX_Z_UNITS = 25;
const MAX_SPEC_BYTES = 200 * 1000; // 200KB; real exports run 15-50KB.

/**
 * Validate a built ArenaSpec before publishing it live (e.g. uploading to
 * PlayFab). Stricter than importArenaJson: rejects rather than silently
 * skipping bad data, since this is the exact payload that will be sent to
 * the server and re-validated there.
 */
export function validateArenaSpec(spec: ArenaSpec): string | null {
  if (JSON.stringify(spec).length > MAX_SPEC_BYTES) {
    return 'Layout is too large to upload.';
  }

  if (!Array.isArray(spec.objects)) {
    return 'Layout has no objects.';
  }

  const placedObjects = spec.objects.filter(o => !SKIP_TYPES.has(o.type));
  if (placedObjects.length === 0) {
    return 'Layout is empty — place at least one bunker before uploading.';
  }

  if (!VALID_TILESETS.includes(spec.tileset as TilesetKey)) {
    return `Unknown tileset "${spec.tileset}".`;
  }

  for (const obj of placedObjects) {
    if (!CATALOG_TYPES.has(obj.type)) {
      return `Unknown bunker type "${obj.type}".`;
    }
    if (Math.abs(obj.position?.x ?? 0) > MAX_X_UNITS || Math.abs(obj.position?.z ?? 0) > MAX_Z_UNITS) {
      return 'One or more bunkers are placed outside the field bounds.';
    }
  }

  return null;
}
